/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { type ApiError } from "@/lib/api/client";
import {
  adminActivityActions,
  adminActivityAnalytics,
  adminArchiveTrack,
  adminCreateTrack,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateTrack,
  adminUpdateUser,
  changePassword,
  createPersonalToken,
  getActivity,
  getDashboard,
  getHealth,
  getMe,
  getMeDetail,
  getTrack,
  listTracks,
  listTrackStatuses,
  login,
  logout,
  type MilestoneInput,
  publishStatus,
  setProgramMilestones,
  signup,
  type SourceRef,
  steercoRefresh,
  submitStatus,
  unpublishStatus,
  updateProfile,
  upsertWeeklyStatus,
} from "@/lib/api/generated";

// ─── Save-error surfacing ──────────────────────────────────────────────────

/**
 * Pull FastAPI's `{ detail: ... }` message out of a thrown SDK error.
 *
 * The client (see `./client`) wraps every non-OK response as `ApiError`
 * (`{ status, body }`), with `body` carrying the backend payload. Returns the
 * `detail` string when present so callers can show the server's own reason
 * (e.g. a 409 conflict message), or `null` to fall back to a generic message.
 */
function apiErrorDetail(error: unknown): string | null {
  const body = (error as ApiError | undefined)?.body;
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
  }
  return null;
}

/**
 * Shared `onError` for mutations backing inline dashboard edits. Surfaces a
 * toast so a failed save is never silent — prefers the server's `detail`,
 * falling back to a generic retry message. `action` names what was being
 * saved, e.g. "updating the track".
 */
function toastSaveError(action: string) {
  return (error: unknown) => {
    toast.error(`Couldn't save — ${action}. Please try again.`, {
      description: apiErrorDetail(error) ?? undefined,
    });
  };
}

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

/**
 * Mint a long-lived personal access token (`POST /auth/token`).
 *
 * Returns `PersonalToken { token, expires_in_days }`. No cache invalidations —
 * this creates a standalone credential, it doesn't change any queried state.
 * The caller is responsible for showing the token once and never persisting it.
 */
export function useCreatePersonalToken() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await createPersonalToken();
      return data;
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Tracks & weekly status ────────────────────────────────────────────────

export function useTracks() {
  return useQuery({
    queryKey: ["tracks"],
    queryFn: async () => {
      const { data } = await listTracks();
      return data ?? [];
    },
  });
}

export function useTrack(trackId: string | null) {
  return useQuery({
    queryKey: ["tracks", trackId],
    queryFn: async () => {
      if (!trackId) {
        return null;
      }
      const { data } = await getTrack({ path: { track_id: trackId } });
      return data;
    },
    enabled: !!trackId,
  });
}

export function useTrackStatuses(trackId: string | null) {
  return useQuery({
    queryKey: ["tracks", trackId, "statuses"],
    queryFn: async () => {
      if (!trackId) {
        return [];
      }
      const { data } = await listTrackStatuses({ path: { track_id: trackId } });
      return data ?? [];
    },
    enabled: !!trackId,
  });
}

/**
 * Fetch weekly statuses for many tracks at once — one query per track id, keyed
 * exactly like `useTrackStatuses` so the caches are shared. Uses `useQueries`
 * (not a loop of `useTrackStatuses`) to stay within the rules of hooks when the
 * id list changes length.
 */
export function useAllTrackStatuses(trackIds: string[]) {
  return useQueries({
    queries: trackIds.map((trackId) => ({
      queryKey: ["tracks", trackId, "statuses"],
      queryFn: async () => {
        const { data } = await listTrackStatuses({ path: { track_id: trackId } });
        return data ?? [];
      },
    })),
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const { data } = await getDashboard();
      return data;
    },
  });
}

export function useAdminCreateTrack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      name: string;
      businessUnit?: string;
      description?: string;
      ownerId?: string;
      targetDate?: string;
      sortOrder?: number;
      stage?: string;
      championNote?: string | null;
      selectedUseCase?: string | null;
      useCasesIdentified?: number | null;
      useCasesPrioritized?: number | null;
      prototypeNote?: string | null;
      prototypeRag?: string | null;
      valueConfidence?: string | null;
      boardReadiness?: string | null;
      boardOutcome?: string | null;
    }) => {
      const { data } = await adminCreateTrack({
        body: {
          name: vars.name,
          business_unit: vars.businessUnit,
          description: vars.description,
          owner_id: vars.ownerId,
          target_date: vars.targetDate,
          sort_order: vars.sortOrder,
          stage: vars.stage,
          champion_note: vars.championNote,
          selected_use_case: vars.selectedUseCase,
          use_cases_identified: vars.useCasesIdentified,
          use_cases_prioritized: vars.useCasesPrioritized,
          prototype_note: vars.prototypeNote,
          prototype_rag: vars.prototypeRag,
          value_confidence: vars.valueConfidence,
          board_readiness: vars.boardReadiness,
          board_outcome: vars.boardOutcome,
        },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useAdminUpdateTrack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      trackId: string;
      name?: string;
      businessUnit?: string | null;
      description?: string | null;
      ownerId?: string | null;
      targetDate?: string;
      sortOrder?: number;
      isActive?: boolean;
      stage?: string;
      championNote?: string | null;
      selectedUseCase?: string | null;
      useCasesIdentified?: number | null;
      useCasesPrioritized?: number | null;
      prototypeNote?: string | null;
      prototypeRag?: string | null;
      valueConfidence?: string | null;
      boardReadiness?: string | null;
      boardOutcome?: string | null;
      // Per-week journey phase tags. COALESCE'd server-side, so send it ONLY
      // when actually editing a phase — omit it for pure scalar edits.
      phaseTimeline?: { week_start: string; phase: string }[];
      // Exec Steerco snapshot object. The generated TrackUpdate body may not
      // expose `steerco` yet (backend added in parallel), so it's spread in
      // below to bypass excess-property checks until the schema regenerates.
      // TODO: once `steerco` lands in the generated schema, fold it into the
      // typed body literal like the other fields.
      steerco?: unknown;
    }) => {
      const { data } = await adminUpdateTrack({
        path: { track_id: vars.trackId },
        body: {
          name: vars.name,
          business_unit: vars.businessUnit,
          description: vars.description,
          owner_id: vars.ownerId,
          target_date: vars.targetDate,
          sort_order: vars.sortOrder,
          is_active: vars.isActive,
          stage: vars.stage,
          champion_note: vars.championNote,
          selected_use_case: vars.selectedUseCase,
          use_cases_identified: vars.useCasesIdentified,
          use_cases_prioritized: vars.useCasesPrioritized,
          prototype_note: vars.prototypeNote,
          prototype_rag: vars.prototypeRag,
          value_confidence: vars.valueConfidence,
          board_readiness: vars.boardReadiness,
          board_outcome: vars.boardOutcome,
          phase_timeline: vars.phaseTimeline,
          ...(vars.steerco !== undefined ? { steerco: vars.steerco } : {}),
        },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: toastSaveError("updating the track"),
  });
}

/**
 * Backfill the Steerco snapshot from the latest tracked data (admin only).
 *
 * Wraps `POST /steerco/refresh` (op `steercoRefresh`). On success it invalidates
 * the dashboard and every per-track query (the `["tracks", …]` prefix also
 * covers each track's `"statuses"` cache) so the Steerco tab re-renders with the
 * synced values. The resolved `SteercoRefreshResult` (`tracks_updated` +
 * `changes[trackName]` diff strings) lets callers report exactly what moved.
 */
export function useRefreshSteerco() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await steercoRefresh();
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/**
 * Bulk-replace the program-wide milestone set. `setProgramMilestones` is a full
 * replace on the server, so callers must send the COMPLETE milestone list.
 */
export function useSetProgramMilestones() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { milestones: MilestoneInput[] }) => {
      const { data } = await setProgramMilestones({ body: vars.milestones });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: toastSaveError("updating milestones"),
  });
}

export function useAdminArchiveTrack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { trackId: string }) => {
      const { data } = await adminArchiveTrack({ path: { track_id: vars.trackId } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUpsertStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      trackId: string;
      weekStart: string;
      rag?: string;
      percentComplete?: number;
      summary?: string | null;
      progress?: string | null;
      blockers?: string | null;
      nextSteps?: string | null;
      source?: string;
      sourceRefs?: SourceRef[];
    }) => {
      const { data } = await upsertWeeklyStatus({
        path: { track_id: vars.trackId },
        body: {
          week_start: vars.weekStart,
          rag: vars.rag,
          percent_complete: vars.percentComplete,
          summary: vars.summary,
          progress: vars.progress,
          blockers: vars.blockers,
          next_steps: vars.nextSteps,
          source: vars.source,
          source_refs: vars.sourceRefs,
        },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId, "statuses"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: toastSaveError("updating the weekly status"),
  });
}

export function useSubmitStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { statusId: string; trackId: string }) => {
      const { data } = await submitStatus({ path: { status_id: vars.statusId } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId, "statuses"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function usePublishStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { statusId: string; trackId: string }) => {
      const { data } = await publishStatus({ path: { status_id: vars.statusId } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId, "statuses"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useUnpublishStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { statusId: string; trackId: string }) => {
      const { data } = await unpublishStatus({ path: { status_id: vars.statusId } });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["tracks"] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId] });
      void queryClient.invalidateQueries({ queryKey: ["tracks", vars.trackId, "statuses"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
